/**
 * GameOverPhase — end-of-game UI (phase ENDED).
 *
 * Layout: final board (1/4 width) | right column (stats, players, monsters).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Board } from '../components/Board/Board.tsx';
import MonsterCircle from '../components/Monsters/MonsterCircle';
import { useGameStateContext } from '../context/useGameStateContext';
import { parseBoardState } from '../utils/boardHelpers';
import { IMAGES } from '../../utils/images';
import { COLORS } from '../../utils/colors';
import type { PlayerColor, PlayerLetter } from '../types/game.types';
import type { SquareData } from '../types/board.types';
import type { FrontendMonster, MonsterDefinition, MonsterTypeId } from '../types/monster.types';
import { getMonsterNames } from '../utils/constants';
import { getMonsterMovement } from '../utils/monsterHelpers';
import { useGameSocket } from '../hooks/useGameSocket';

import './GameOverPhase.css';
import { useNavigate } from 'react-router-dom';

function monsterToCircleDef(m: { id: number; type: number }): MonsterDefinition {
    return { id: m.type as MonsterTypeId } as MonsterDefinition;
}

function toFrontendMonster(
    monster: { id: number; type: number; position: { row: number; col: number }; alive: boolean },
    player: PlayerLetter
): FrontendMonster {
    const info = getMonsterNames(monster.type);
    const type = monster.type as MonsterTypeId;
    return {
        id: monster.id,
        type,
        name: (info?.name ?? 'Leviathan') as FrontendMonster['name'],
        player,
        position: { ...monster.position },
        alive: monster.alive,
        guess: null,
        xed: [],
        movement: getMonsterMovement(type),
        imageUrl: null,
    };
}

export const GameOverPhase: React.FC = () => {
    const {
        gameState,
        board,
        myPlayerLetter,
        roomCode,
        theme,
        playerA,
        playerB,
    } = useGameStateContext();
    const { getHistory } = useGameSocket();

    const navigate = useNavigate();
    const [selectedPiece, setSelectedPiece] = useState<FrontendMonster | null>(null);
    const [viewingTurn, setViewingTurn] = useState(0);
    const [historyBoard, setHistoryBoard] = useState<typeof board>(null);
    const [historyLastMove, setHistoryLastMove] = useState<NonNullable<typeof gameState>['gameHistory']['moves'][number] | null>(null);
    const latestHistoryRequestTurnRef = useRef<number | null>(null);

    const youWin = gameState?.winner != null && myPlayerLetter != null && gameState.winner === myPlayerLetter;

    const lastMove = useMemo(() => {
        const moves = gameState?.gameHistory.moves ?? [];
        return moves.length > 0 ? moves[moves.length - 1] : null;
    }, [gameState]);
    const maxViewTurn = gameState?.gameHistory.moves.length ?? 0;
    const effectiveRoomCode = roomCode ?? gameState?.roomCode ?? '';
    const isPresent = viewingTurn === maxViewTurn;
    const displayBoard = isPresent ? board : (historyBoard ?? board);
    const displayLastMove = isPresent ? lastMove : historyLastMove;
    const squares = useMemo(() => {
        if (!displayBoard) return [];
        return parseBoardState(displayBoard);
    }, [displayBoard]);

    const playerAColor: PlayerColor = (playerA?.color as PlayerColor) ?? 'red';
    const playerBColor: PlayerColor = (playerB?.color as PlayerColor) ?? 'blue';

    useEffect(() => {
        setViewingTurn(maxViewTurn);
    }, [maxViewTurn]);

    useEffect(() => {
        if (viewingTurn === maxViewTurn) {
            setHistoryBoard(null);
            setHistoryLastMove(null);
            return;
        }
        if (!effectiveRoomCode) return;

        latestHistoryRequestTurnRef.current = viewingTurn;
        getHistory({ roomCode: effectiveRoomCode, turn: viewingTurn }, (res) => {
            if (latestHistoryRequestTurnRef.current !== viewingTurn) return;
            if (res.error || !res.board) {
                setHistoryBoard(null);
                setHistoryLastMove(null);
                return;
            }
            setHistoryBoard(res.board);
            setHistoryLastMove(res.lastMove ?? null);
        });
    }, [viewingTurn, maxViewTurn, effectiveRoomCode, getHistory]);

    if (!gameState || gameState.phase !== 'ENDED' || gameState.winner == null || !myPlayerLetter) {
        return <div className="game_over_phase" />;
    }

    const borderFor = (letter: PlayerLetter) =>
        COLORS.player_colors[letter === 'A' ? playerAColor : playerBColor];

    const handleBoardSquareClick = useCallback(
        (square: SquareData) => {
            if (square.type !== 'monster' || !square.player) return;
            const monsters =
                square.player === 'A'
                    ? gameState.players.A.monsters
                    : gameState.players.B.monsters;
            const clickedMonster = monsters.find(
                (m) => m.position.row === square.row && m.position.col === square.col
            );
            if (!clickedMonster) return;
            setSelectedPiece(toFrontendMonster(clickedMonster, square.player));
        },
        [gameState]
    );

    const renderPlayerRow = (letter: PlayerLetter) => {
        const roomPlayer = letter === 'A' ? playerA : playerB;
        const monsters =
            letter === 'A' ? gameState.players.A.monsters : gameState.players.B.monsters;

        return (
            <div
                key={letter}
                className="game_over_player_container"
                style={{ borderColor: borderFor(letter) }}
            >
                <div className="game_over_player_avatar">
                    <img
                        src={IMAGES.profile_pics['default.png']}
                        className="game_over_player_avatar_img"
                        alt=""
                    />
                </div>
                <div className="game_over_player_text_block">
                    <p className="game_over_player_name">{roomPlayer?.username ?? `Player ${letter}`}</p>
                    <p className="game_over_player_title">{roomPlayer?.title || 'Cadet'}</p>
                </div>
                <div className="game_over_monster_strip" aria-hidden>
                    {[...monsters]
                        .sort((a, b) => a.id - b.id)
                        .map((m) => {
                            const isSelected =
                                selectedPiece?.player === letter && selectedPiece?.id === m.id;

                            const opacity = m.alive ? 1 : 0.55;
                            
                            return (
                                <MonsterCircle
                                    monster={monsterToCircleDef(m)}
                                    theme={theme}
                                    disabled={false}
                                    hidden={false}
                                    onClick={() => setSelectedPiece(toFrontendMonster(m, letter))}
                                    style={{ cursor: 'pointer', opacity }}
                                    imageStyle={{
                                        borderColor: isSelected ? COLORS.selected[theme] : borderFor(letter),
                                    }}
                                />
                            );
                        })}
                </div>
            </div>
        );
    };

    return (
        <div
            className="game_over_phase_container"
            style={{
                backgroundImage: `url(${IMAGES.backgrounds.room})`,
            }}
        >
            <div className="game_over_board_column">
                <div className="game_over_board_wrap">
                    <Board
                        squares={squares}
                        onSquareClick={handleBoardSquareClick}
                        selectedPiece={selectedPiece}
                        disabled={false}
                        phase="ENDED"
                        theme={theme}
                        lastMove={displayLastMove}
                        playerLetter={myPlayerLetter}
                        playerAColor={playerAColor}
                        playerBColor={playerBColor}
                    />
                </div>
                <div className="game_over_history_controls">
                    <button
                        type="button"
                        className="game_over_history_button"
                        onClick={() => setViewingTurn((prev) => Math.max(0, prev - 1))}
                        disabled={viewingTurn <= 0}
                    >
                        ←
                    </button>
                    <span className="game_over_history_turn">Turn {viewingTurn}</span>
                    <button
                        type="button"
                        className="game_over_history_button"
                        onClick={() => setViewingTurn((prev) => Math.min(maxViewTurn, prev + 1))}
                        disabled={viewingTurn >= maxViewTurn}
                    >
                        →
                    </button>
                </div>
            </div>

            <div className="game_over_right_column">
                <div className="game_over_top_bar">
                    <p className="game_over_win_text">{youWin ? 'You Win!' : 'You Lose!'}</p>
                    <div className="game_over_logo_wrap">
                        <img src={IMAGES.logo} className="game_over_logo" alt="" />
                    </div>
                </div>

                <div className="game_over_bottom_container">
                    {renderPlayerRow('A')}
                    {renderPlayerRow('B')}
                </div>

                <div className="game_over_spacer" />

                <div className="game_over_button_container">
                    <button className="game_over_button" onClick={() => { navigate('/home'); }}>Leave Game</button>
                </div>
            </div>
        </div>
    );
};

export default GameOverPhase;
